'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

// lib
import AppShell from '@components/ui/app-shell'
import PageHeader from '@components/ui/page-header'
import {
  IMemberOpportunity,
  useMemberOpportunitiesStore
} from '@store/MemberOpportunities'
import { ChatLocationState } from '../chat/room/chat-room'
import OpportunitiesSection, { TypeTile } from './opportunities-section.web'
import RejectOpportunityModal from './reject-opportunity-modal.web'
import { ALL_FILTER, OPPORTUNITY_TYPE_KEYS } from './data/taxonomy'
import { buildChatMessage } from './opportunity-text'

/* -----------------------------------------------------------------------------
 * constants
 * -------------------------------------------------------------------------- */

const PAGE_SIZE = 25

const toParam = (filter: string) => (filter === ALL_FILTER ? undefined : filter)

// rows flagged between two page requests push the list down, which can
// repeat a row of the previous page; a row repeated within one page is
// listed once as well
const appendNew = (
  current: IMemberOpportunity[],
  next: IMemberOpportunity[]
) => {
  const seen = new Set(current.map(item => item._id))
  return [
    ...current,
    ...next.filter(item => {
      if (seen.has(item._id)) {
        return false
      }
      seen.add(item._id)
      return true
    })
  ]
}

/* -----------------------------------------------------------------------------
 * Dashboard
 * -------------------------------------------------------------------------- */

function Dashboard() {
  const navigate = useNavigate()
  const { getMemberOpportunities, updateOpportunityStatus } =
    useMemberOpportunitiesStore.getState()

  const [opportunities, setOpportunities] = useState<IMemberOpportunity[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false)
  const [hasFailed, setHasFailed] = useState(false)
  const [typeFilter, setTypeFilter] = useState(ALL_FILTER)
  const [priorityFilter, setPriorityFilter] = useState(ALL_FILTER)
  const [typeCounts, setTypeCounts] = useState<Record<string, number | null>>(
    {}
  )

  const [pendingReject, setPendingReject] = useState<IMemberOpportunity | null>(
    null
  )
  const [rejectError, setRejectError] = useState<string | null>(null)
  const [isRejecting, setIsRejecting] = useState(false)

  // a filter change while a page is on its way makes that page stale
  const requestId = useRef(0)

  const loadFirstPage = useCallback(async () => {
    const id = ++requestId.current
    setIsLoading(true)
    setHasFailed(false)
    const response = await getMemberOpportunities({
      opportunityType: toParam(typeFilter),
      pageSize: PAGE_SIZE,
      priority: toParam(priorityFilter)
    })
    if (id !== requestId.current) {
      return
    }
    setOpportunities(appendNew([], response?.results ?? []))
    setTotal(response?.total_events ?? 0)
    setPage(0)
    setHasNextPage(Boolean(response?.has_next_page))
    setHasFailed(response === null)
    setIsFetchingNextPage(false)
    setIsLoading(false)
  }, [getMemberOpportunities, priorityFilter, typeFilter])

  // one row per type is enough to read its total
  const loadTypeCounts = useCallback(async () => {
    setTypeCounts({})
    const responses = await Promise.all(
      OPPORTUNITY_TYPE_KEYS.map(type =>
        getMemberOpportunities({ opportunityType: type, pageSize: 1 })
      )
    )
    setTypeCounts(
      Object.fromEntries(
        OPPORTUNITY_TYPE_KEYS.map((type, index) => [
          type,
          responses[index]?.total_events ?? null
        ])
      )
    )
  }, [getMemberOpportunities])

  useEffect(() => {
    loadFirstPage()
  }, [loadFirstPage])

  useEffect(() => {
    loadTypeCounts()
  }, [loadTypeCounts])

  const loadNextPage = async () => {
    if (!hasNextPage || isFetchingNextPage) {
      return
    }
    const id = requestId.current
    setIsFetchingNextPage(true)
    const response = await getMemberOpportunities({
      opportunityType: toParam(typeFilter),
      page: page + 1,
      pageSize: PAGE_SIZE,
      priority: toParam(priorityFilter)
    })
    if (id !== requestId.current) {
      return
    }
    // a failed page keeps what is listed and lets the professional try again
    if (response) {
      setOpportunities(current => appendNew(current, response.results))
      setTotal(response.total_events)
      setPage(response.page)
      setHasNextPage(response.has_next_page)
    }
    setIsFetchingNextPage(false)
  }

  // types without a name get no tile: finding them would mean reading every
  // page, and their rows still show under All
  const typeTiles: TypeTile[] = OPPORTUNITY_TYPE_KEYS.map(type => ({
    count: typeCounts[type] ?? null,
    type
  }))

  const openReject = (opportunity: IMemberOpportunity) => {
    setRejectError(null)
    setPendingReject(opportunity)
  }

  const closeReject = () => {
    if (!isRejecting) {
      setPendingReject(null)
    }
  }

  // a resolved opportunity leaves the list and its tile right away
  const removeOpportunity = (opportunity: IMemberOpportunity) => {
    setOpportunities(current =>
      current.filter(item => item._id !== opportunity._id)
    )
    setTotal(current => Math.max(current - 1, 0))
    const type = opportunity.opportunity_type ?? ''
    setTypeCounts(current =>
      typeof current[type] === 'number'
        ? { ...current, [type]: Math.max(current[type] - 1, 0) }
        : current
    )
  }

  const confirmReject = async () => {
    if (!pendingReject?.user_id) {
      return
    }
    setIsRejecting(true)
    setRejectError(null)
    const result = await updateOpportunityStatus(
      pendingReject.user_id,
      pendingReject._id,
      'DISMISSED'
    )
    setIsRejecting(false)
    if (!result.success) {
      setRejectError('The opportunity could not be rejected. Please try again.')
      return
    }
    removeOpportunity(pendingReject)
    setPendingReject(null)
  }

  const messageMember = (opportunity: IMemberOpportunity) => {
    const state: ChatLocationState = {
      draftMessage: buildChatMessage(opportunity),
      opportunityId: opportunity._id
    }
    navigate(`/chat/${opportunity.user_id}`, { state })
  }

  return (
    <AppShell activePage='dashboard' label='Dashboard'>
      <PageHeader eyebrow='Clinical operations' title='Dashboard' />

      <OpportunitiesSection
        hasFailed={hasFailed}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isFiltered={
          typeFilter !== ALL_FILTER || priorityFilter !== ALL_FILTER
        }
        isLoading={isLoading}
        onLoadNextPage={loadNextPage}
        onMessage={messageMember}
        onPriorityFilter={setPriorityFilter}
        onReject={openReject}
        onResetFilters={() => {
          setTypeFilter(ALL_FILTER)
          setPriorityFilter(ALL_FILTER)
        }}
        onRetry={() => {
          loadFirstPage()
          loadTypeCounts()
        }}
        onTypeFilter={setTypeFilter}
        priorityFilter={priorityFilter}
        resultCount={total}
        rows={opportunities}
        typeFilter={typeFilter}
        typeTiles={typeTiles}
      />

      <RejectOpportunityModal
        error={rejectError}
        isSubmitting={isRejecting}
        onCancel={closeReject}
        onConfirm={confirmReject}
        opportunity={pendingReject}
      />
    </AppShell>
  )
}

export default Dashboard
